/**
 * EditMcpConnectionDialog — first-class edit surface for a BYO MCP connection
 * stored under `agentMcpConnections` in `connections.json` (or the draft file
 * in draft mode).
 *
 * Reuses `McpAddServerForm` in `mode='edit'`: the form seeds itself from the
 * existing `McpConnection`, revalidates the endpoint on submit (or skips the
 * probe when no workflow context is available), and delegates persistence to
 * the caller-supplied `onSave` callback so the draft/prod routing owned by
 * `useConnectionsEditor.handleSaveLocalConnection` stays authoritative.
 *
 * The dialog is a thin chrome wrapper; the form owns its own layout, error
 * banners, and primary-action button. We provide the modal container + title
 * so the surface matches the rest of the ConnectionList editor.
 */

import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { McpAddServerForm } from '@/components/WorkflowDesigner/panels/McpAddServerForm';
import type { McpConnection } from '@/types/logicApps';

const useStyles = makeStyles({
  dialogSurface: {
    maxWidth: '720px',
    minWidth: '560px',
  },
  body: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
});

export interface EditMcpConnectionDialogProps {
  open: boolean;
  onClose: () => void;
  connectionKey: string;
  connection: McpConnection;
  /**
   * Persist an updated MCP connection record. Draft/prod routing is the
   * caller's responsibility — typically this wires straight to
   * `useConnectionsEditor.handleSaveLocalConnection(id, 'mcp', record)`.
   */
  onSave: (recordKey: string, record: McpConnection) => Promise<void>;
}

export const EditMcpConnectionDialog = ({
  open,
  onClose,
  connectionKey,
  connection,
  onSave,
}: EditMcpConnectionDialogProps) => {
  const styles = useStyles();

  return (
    <Dialog
      open={open}
      onOpenChange={(_, data) => {
        if (!data.open) onClose();
      }}
      modalType="modal"
    >
      <DialogSurface className={styles.dialogSurface}>
        <DialogBody>
          <DialogTitle>Edit MCP connection</DialogTitle>
          <DialogContent className={styles.body}>
            <McpAddServerForm
              mode="edit"
              initialRecordKey={connectionKey}
              initialConnection={connection}
              onCancel={onClose}
              onAdded={onClose}
              onEditSave={onSave}
            />
          </DialogContent>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

EditMcpConnectionDialog.displayName = 'EditMcpConnectionDialog';
